import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, Award, Check, ChevronDown, Copy, MapPin, Share2 } from "lucide-react";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { cn, getFileExtension } from "@/modern/utils";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { SpotIncentive } from "@/bryntum-lib/models/SpotIncentive";
import { useFileInsights } from "@/react/hooks/entityHooks/useFileInsights";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { KeyFindingsTable } from "./KeyFindingsTable";
import { IncentivePointsSection } from "./IncentivePointsSection";
import { PublicShareSection, useFilePublicShareLink } from "./PublicShareSection";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";

type SummaryShareSection = "share" | "points" | "findings";

const ACCORDION_EASE = [0.32, 0.72, 0, 1] as const;

function RailAccordion({
  id,
  title,
  icon,
  count,
  open,
  onOpenChange,
  children,
}: {
  id: string;
  title: string;
  icon: ReactNode;
  count?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 overflow-x-hidden border-b border-border/40">
      <button
        type="button"
        id={id}
        aria-expanded={open}
        className={cn(
          "flex w-full min-w-0 items-center gap-2 py-3 text-left outline-none",
          "focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
        )}
        onClick={() => onOpenChange(!open)}
      >
        <span className="shrink-0 text-muted-foreground">{icon}</span>
        <span className="min-w-0 flex-1 truncate text-base font-normal tracking-tight text-foreground">
          {title}
        </span>
        {typeof count === "number" ? (
          <Badge variant="muted" className="h-5 min-w-5 shrink-0 px-1.5 text-xs tabular-nums">
            {count}
          </Badge>
        ) : null}
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform duration-200",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key={id}
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.28, ease: ACCORDION_EASE }}
            className="w-full min-w-0 overflow-x-hidden overflow-y-clip"
          >
            <div className="min-w-0 pb-4">{children}</div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export const SummarySharePanel = () => {
  const {
    file,
    project,
    summaryTab,
    setSummaryTab,
    handleCloseSummarySharePanel,
  } = useFileDetailModal();
  const { shareLink, copyShareLink } = useFilePublicShareLink();
  const [linkCopied, setLinkCopied] = useState(false);
  const [openSection, setOpenSection] = useState<SummaryShareSection | null>("share");
  const { getInsights } = useFileInsights();
  const { version: spotVersion } = useSwivuStore<SpotIncentive>({
    storeId: STORE_KEYS.spotIncentiveStore,
  });
  const { version: insightsVersion } = useSwivuStore({ storeId: STORE_KEYS.insightsStore });

  const isPDF = file ? getFileExtension(file.name) === "pdf" : false;
  const isImage = Boolean(file && isImageFileTyped(file) && !isPDF);

  const pointsCount = useMemo(() => {
    if (!project || !file?.id) return 0;
    const store = project.spotIncentiveStore;
    if (!store) return 0;
    return (
      store.query(
        (incentive: SpotIncentive) => incentive.fileId === file.id && !incentive.archived,
      ) as SpotIncentive[]
    ).length;
  }, [project, file?.id, spotVersion]);

  const findingsCount = useMemo(() => {
    if (!file?.id) return 0;
    return getInsights(file.id).length;
  }, [file?.id, getInsights, insightsVersion]);

  useEffect(() => {
    if (!file) return;
    if (summaryTab === "details") {
      setSummaryTab("share");
    }
  }, [file, summaryTab, setSummaryTab]);

  const openAccordion = (section: SummaryShareSection) => {
    setOpenSection(section);
    setSummaryTab(section);
  };

  const handleAccordionChange = (section: SummaryShareSection, next: boolean) => {
    if (next) {
      openAccordion(section);
      return;
    }
    // Allow collapsing the open section so none remain expanded.
    if (openSection === section) {
      setOpenSection(null);
    }
  };

  if (!file) return null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-x-hidden">
      <div className="flex shrink-0 items-center gap-1.5 pb-2.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-9 shrink-0 rounded-full"
          onClick={handleCloseSummarySharePanel}
          aria-label="Back"
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h3 className="min-w-0 flex-1 truncate text-xl font-normal tracking-tight text-foreground">
          {isImage ? "Summary & Share" : "Share"}
        </h3>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
        {isImage ? (
          <div className="min-w-0">
            <RailAccordion
              id="summary-share-whats-shared"
              title="What's shared"
              icon={<Share2 className="size-3.5" />}
              open={openSection === "share"}
              onOpenChange={(next) => handleAccordionChange("share", next)}
            >
              <PublicShareSection
                onOpenPoints={() => openAccordion("points")}
                onOpenFindings={() => openAccordion("findings")}
              />
            </RailAccordion>
            <RailAccordion
              id="summary-share-points"
              title="Points"
              icon={<Award className="size-3.5" />}
              count={pointsCount}
              open={openSection === "points"}
              onOpenChange={(next) => handleAccordionChange("points", next)}
            >
              <IncentivePointsSection compact />
            </RailAccordion>
            <RailAccordion
              id="summary-share-findings"
              title="Findings"
              icon={<MapPin className="size-3.5" />}
              count={findingsCount}
              open={openSection === "findings"}
              onOpenChange={(next) => handleAccordionChange("findings", next)}
            >
              <KeyFindingsTable compact />
            </RailAccordion>
          </div>
        ) : (
          <PublicShareSection />
        )}
      </div>

      <div className="shrink-0 border-t border-border/40 bg-card pt-3">
        <div className="flex min-w-0 items-stretch gap-2">
          <Input
            readOnly
            inputSize="sm"
            value={shareLink}
            placeholder="Share link unavailable"
            aria-label="Public share link"
            className="min-w-0 flex-1 truncate rounded-md border border-input bg-muted/50 text-sm text-foreground shadow-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring focus-visible:ring-offset-0"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            type="button"
            variant="default"
            className="h-9 shrink-0 gap-1.5 rounded-md px-3 text-sm"
            disabled={!shareLink}
            onClick={() => {
              void (async () => {
                const ok = await copyShareLink();
                if (!ok) return;
                setLinkCopied(true);
                window.setTimeout(() => setLinkCopied(false), 1500);
              })();
            }}
          >
            {linkCopied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {linkCopied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>
    </div>
  );
};
